/**
 * Timezone-aware date formatting utilities.
 * All timestamps stored in the database are UTC.
 * Pass the logged-in user's IANA timezone (e.g. 'Europe/Stockholm') to display
 * them in the correct local time.
 */

const DEFAULT_TZ = 'UTC';

/** Format a UTC date string into the user's local timezone. */
export function formatInTZ(
    dateStr: string | number | Date,
    timezone: string | undefined | null,
    options?: Intl.DateTimeFormatOptions
): string {
    const tz = timezone || DEFAULT_TZ;
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return String(dateStr);
    return d.toLocaleString(undefined, {
        timeZone: tz,
        year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', second: '2-digit',
        ...options,
    });
}

/** Format only the time part in the user's timezone. */
export function formatTimeInTZ(
    dateStr: string | number | Date,
    timezone: string | undefined | null
): string {
    const tz = timezone || DEFAULT_TZ;
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return String(dateStr);
    return d.toLocaleTimeString(undefined, { timeZone: tz });
}

/** Returns sorted list of all IANA timezone names supported by the browser. */
export function getAllTimezones(): string[] {
    try {
        return (Intl as any).supportedValuesOf('timeZone') as string[];
    } catch {
        // Fallback for older browsers
        return [
            'UTC',
            'Europe/London', 'Europe/Stockholm', 'Europe/Oslo', 'Europe/Helsinki',
            'Europe/Berlin', 'Europe/Paris', 'Europe/Madrid', 'Europe/Rome',
            'Europe/Amsterdam', 'Europe/Brussels', 'Europe/Zurich',
            'America/New_York', 'America/Chicago', 'America/Denver',
            'America/Los_Angeles', 'America/Toronto', 'America/Sao_Paulo',
            'Asia/Dubai', 'Asia/Kolkata', 'Asia/Shanghai', 'Asia/Tokyo',
            'Asia/Singapore', 'Australia/Sydney', 'Pacific/Auckland',
        ];
    }
}
